import {
  ARTICLE_INNER_CLASS,
  ARTICLE_WRAPPER_CLASS,
  H2_CLASS,
  P_CLASS,
  PULL_QUOTE_CLASS,
} from "../prose";
import { UseCaseHero } from "../UseCaseHero";
import { UseCaseLayout } from "../UseCaseLayout";
import { UseCaseVideo } from "../UseCaseVideo";

const TITLE =
  "Dose Calculations, Fluid Math, Treatment Plans — All Within Companion";
const DECK =
  "Watch how one vet used Companion to build a full V/D treatment plan and calculate a gabapentin course, all from one conversation with Companion.";

export function MedicalMathPage() {
  return (
    <UseCaseLayout>
      <UseCaseHero title={TITLE} deck={DECK} />
      <UseCaseVideo
        embedUrl="https://www.youtube.com/embed/sPB0_e0usJc"
        title={TITLE}
      />
      <article className={ARTICLE_WRAPPER_CLASS}>
        <div className={ARTICLE_INNER_CLASS}>
          <h2 className={H2_CLASS}>The Problem</h2>

          <p className={P_CLASS}>
            Calculations aren&apos;t the hard part. What&apos;s annoying is the
            workflow around it.
          </p>

          <p className={P_CLASS}>
            A V/D case comes in and you need a fluid rate, a Cerenia dose,
            gabapentin for three days. So you&apos;re pulling up a calculator,
            maybe checking a dosing chart, maybe opening a separate reference.
            Then you come back to your notes and type it all in. It works, but
            it&apos;s a lot of tab-switching for something you do all the time.
          </p>

          <h2 className={H2_CLASS}>How I Addressed It</h2>

          <p className={P_CLASS}>
            I wanted to see what would happen if I just stayed in Scribenote for
            the whole thing. So I asked Companion to do the math for me.
            That&apos;s the video above.
          </p>

          <p className={P_CLASS}>
            I gave it one line: “Colby, canine, 20 kg, LRS maintenance, Cerenia
            injectable, IVC, CBC/chem, 2-view rads.” Just the basics.
          </p>

          <p className={P_CLASS}>
            Companion came back with the full treatment plan. Doses calculated,
            volumes in mL, laid out in a table. I didn&apos;t leave Scribenote,
            didn&apos;t open a calculator, didn&apos;t look anything up.
          </p>

          <p className={P_CLASS}>
            Then I threw gabapentin at it: 10 mg/kg, twice daily, three days. It
            gave me capsule counts, liquid volumes, totals for the full course.
            All right there.
          </p>

          <h2 className={H2_CLASS}>Why This Matters</h2>

          <p className={P_CLASS}>
            The thing that stood out to me is that Companion shows its work. I
            could see how it got to the numbers, check the logic, and change
            something if it didn&apos;t fit my patient. A tool you can&apos;t
            verify is a tool you&apos;ll second-guess, and that defeats the
            purpose.
          </p>

          <p className={P_CLASS}>
            None of this replaces your judgment. You&apos;re still deciding
            what&apos;s right for your patient. Companion just keeps you from
            bouncing between apps to get the math done.
          </p>

          <blockquote className={PULL_QUOTE_CLASS}>
            “More time on Colby, less time switching tabs. That&apos;s kind of
            the whole point.”
          </blockquote>

          <h2 className={H2_CLASS}>Try It Yourself</h2>

          <p className={P_CLASS}>
            Next time you&apos;ve got a workup with a few moving parts, try
            giving Companion the basics. Patient, weight, what you want to run.
            See what it gives you back.
          </p>

          <p className={P_CLASS}>
            If you end up finding a cool way to use it, send it over to{" "}
            <a
              href="mailto:submissions@scribenote.com"
              className="font-semibold text-indigo-600 underline underline-offset-2"
            >
              submissions@scribenote.com
            </a>{" "}
            or tag us on social. We love seeing how other vets put Companion to
            work.
          </p>
        </div>
      </article>
    </UseCaseLayout>
  );
}
